import { inject } from '@angular/core';
import { map, tap } from 'rxjs';
import { patchState, signalStore, withMethods } from '@ngrx/signals';
import {
  removeEntity,
  updateEntity,
  withEntities,
} from '@ngrx/signals/entities';
import { withCursorPagination } from '@dgacontrol-frontend/shared-util-cursor-pagination';

import { BillingQuoteService } from './generated/billing-quote/billing-quote.service';
import type {
  BillingV1QuotesListParams,
  PatchedQuoteUpdateRequestV1Request,
  QuoteConversionRemovalRequestV1Request,
  QuoteConversionRequestV1Request,
  QuoteCreateRequestV1Request,
  QuoteV1,
} from './generated/types';

/**
 * TARG-4 pure: injects `BillingQuoteService` directly, no facade. Same shape as
 * `SubscriptionsStore`: cursor list for `dt-quotes` (component-driven `load`)
 * plus the header CRUD of the quote modal (`frm-quote`). Works and lines live in
 * their own sub-resources.
 *
 * `createQuote` does not reload the list: the modal stays open on the new quote,
 * and `dt-quotes` reloads when the modal closes.
 */
export const QuotesStore = signalStore(
  withEntities<QuoteV1>(),
  withCursorPagination<QuoteV1, BillingV1QuotesListParams>(() => {
    const api = inject(BillingQuoteService);
    // The Orval page already is a CursorPage ({results, next, previous}).
    return (query, cursor) =>
      api.billingV1QuotesList({ ...query, cursor: cursor ?? undefined });
  }),
  withMethods((store, api = inject(BillingQuoteService)) => {
    const syncQuote = (quote: QuoteV1) => {
      patchState(store, updateEntity({ id: quote.id, changes: quote }));
    };
    return {
      createQuote(body: QuoteCreateRequestV1Request) {
        return api.billingV1QuotesCreate(body).pipe(map((res) => res.quote));
      },
      updateQuote(identity: string, body: PatchedQuoteUpdateRequestV1Request) {
        return api.billingV1QuotesPartialUpdate({ identity }, body).pipe(
          map((res) => res.quote),
          tap(syncQuote),
        );
      },
      deleteQuote(identity: string, id: number) {
        return api.billingV1QuotesDestroy({ identity }).pipe(
          tap(() => {
            patchState(store, removeEntity(id));
          }),
        );
      },
      /** M-bill-16: the quote becomes its client, works, charges and subscription,
       *  all or nothing. It comes back converted: the list row follows. */
      convertQuote(identity: string, body: QuoteConversionRequestV1Request) {
        return api.billingV1QuotesConversionCreate({ identity }, body).pipe(
          map((res) => res.conversion),
          tap((conversion) => {
            syncQuote(conversion.quote);
          }),
        );
      },
      /** Part d: what the conversion made and is still there, each piece with what
       *  blocks it. 409 `quote-not-converted` once nothing is left. */
      conversionPieces: (identity: string) =>
        api
          .billingV1QuotesConversionRetrieve({ identity })
          .pipe(map((res) => res.conversion_pieces)),
      /** Removes the marked pieces, all or nothing, and answers what is left. When
       *  nothing is, the quote is not converted any more: `dt-quotes` reloads on close. */
      removeConversionPieces: (identity: string, body: QuoteConversionRemovalRequestV1Request) =>
        api
          .billingV1QuotesConversionRemovalsCreate({ identity }, body)
          .pipe(map((res) => res.conversion_pieces)),
      /** A line mutation answered the quote with its totals recomputed. */
      syncQuote,
      /** DELETE of a line answers 204 with no body: the totals have to be re-read. */
      refreshQuote: (identity: string) =>
        api.billingV1QuotesRetrieve({ identity }).pipe(
          map((res) => res.quote),
          tap(syncQuote),
        ),
      // GET → binary PDF (`responseType: 'blob'` in the generated client). Nothing
      // to patch: the PDF is rendered on the fly and never stored.
      quotePdf: (identity: string) => api.billingV1QuotesPdfRetrieve({ identity }),
    };
  }),
);
